import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

import Header from "./components/Header";
import Footer from "./components/Footer";

const Program = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [resources, setResources] = useState({});
    const [errors, setErrors] = useState("");

    const [formSettings, setFormSettings] = useState(null);
    const [ticketData, setTicketData] = useState({});

    // Freitag
    const [prePackageOpen, setPrePackageOpen] = useState(false);
    const [prePackage, setPrePackage] = useState("");
    const [freitagAbend, setFreitagAbend] = useState(false);
    const [nachtwachter, setNachtwachter] = useState(false);

    // Samstag optional
    const [schiffsausflug, setSchiffsausflug] = useState(false);
    const [nachtessenShow, setNachtessenShow] = useState(false);

    // Sonntag
    const [farwell, setFarwell] = useState(false);
    const [anschlussPackageOpen, setAnschlussPackageOpen] = useState(false);
    const [anschlussPackage, setAnschlussPackage] = useState("");

    // Verpflegung
    const [verpflegung, setVerpflegung] = useState("Alles");
    const [hasIntoleranzen, setHasIntoleranzen] = useState(false);
    const [intoleranzen, setIntoleranzen] = useState("");

    const navigate = useNavigate();
    const baseUrl = process.env.REACT_APP_BASEURL_API;
    const { language } = useParams();

    const languageId = { de: 1, fr: 2, en: 3, it: 4 }[language] ?? 1;

    const orderId = sessionStorage.getItem("orderId");
    const buyerInfo = JSON.parse(sessionStorage.getItem("buyerInfo") || "null");
    const companionCount = parseInt(sessionStorage.getItem("companionCount") || "0");

    useEffect(() => {
        if (!buyerInfo) {
            navigate(companionCount > 0 ? `/${language}/participant/companion/${companionCount}` : `/${language}/participant`);
            return;
        }
    }, []);

    useEffect(() => {
        const token = sessionStorage.getItem("token");
        if (token) {
            axios.defaults.headers.common["Authorization"] = "Bearer " + token;
        }
        requestResources();
        loadSettingsAndTickets();
    }, []);

    useEffect(() => {
        requestResources();
    }, [language]);

    const requestResources = async () => {
        try {
            const res = await axios.get(`form/resources/${language}`);
            setResources(res.data?.translation);
        } catch (error) {
            console.error(error);
        }
    };

    const loadSettingsAndTickets = async () => {
        try {
            const res = await axios.get("form/formsettings");
            const settings = res.data;
            setFormSettings(settings);
            
            let currentOrderId = sessionStorage.getItem("orderId");
            try {
                //check if order still exists
                await axios.get(`${baseUrl}/ShopBasket/Order/${currentOrderId}`);
            } catch {
                //if order doesnt exist start one
                const orderRes = await axios.post(`${baseUrl}/ShopBasket/Order`, {
                    affiliateShopId: null,
                    currency: "CHF",
                    tenantId: 1,
                    pointOfSaleId: settings.posId,
                    abbreviation: "",
                    paymentType: 1,
                    deliveryType: 1,
                });
                currentOrderId = orderRes.data.id;
                sessionStorage.setItem("orderId", currentOrderId);
            }

            await loadAllTicketData(settings, currentOrderId);
        } catch (error) {
            console.error(error);
        }
    };

    const loadAllTicketData = async (settings, currentOrderId) => {
        const ids = [
            settings.prePackageRheinfallTicketTypeId,
            settings.prePackageWeinprobeTicketTypeId,
            settings.prePackageFuehrungSteinTicketTypeId,
            settings.freitagAbendTicketTypeId,
            settings.nachtwachterTicketTypeId,
            settings.kafiGipfeliTicketTypeId,
            settings.schiffsausflugTicketTypeId,
            settings.nachtessenShowTicketTypeId,
            settings.farwellTicketTypeId,
            settings.anschlussRheinfallTicketTypeId,
            settings.anschlussWeinprobeTicketTypeId,
            settings.anschluss360TicketTypeId,
            settings.anschlussStadtfuehrungTicketTypeId,
            settings.anschlussFuehrungSteinTicketTypeId,
        ].filter(id => id && id > 1);

        const results = {};
        await Promise.all(ids.map(async (id) => {
            try {
                const [typeRes, availRes, infosRes] = await Promise.all([
                    axios.get(`${baseUrl}/TicketType/${id}`),
                    axios.get(`${baseUrl}/TicketType/${id}/Availability?orderId=${currentOrderId}`),
                    axios.get(`${baseUrl}/TicketType/${id}/Infos`)
                ]);
                const infos = infosRes.data.ticketTypeInfos ?? [];
                const info = infos.find(i => i.languageId === languageId)
                    ?? infos.find(i => i.languageId === 1)
                    ?? infos[0];
                results[id] = {
                    price: typeRes.data.priceInclServiceFee ?? typeRes.data.price ?? 0,
                    availability: availRes.data,
                    name: info?.name ?? "",
                    description: info?.description ?? ""
                };
            } catch (err) {
                console.error(`Error loading ticket type ${id}:`, err);
                results[id] = { price: null, availability: { status: 1 }, name: "" };
            }
        }));

        setTicketData(results);
    };

    const getDescription = (id) => ticketData[id]?.description ?? "";

    const getName = (id) => {
        const name = ticketData[id]?.name ?? "";
        const colonIndex = name.indexOf(": ");
        return colonIndex !== -1 ? name.slice(colonIndex + 2) : name;
    };

    const getPrice = (id) => {
        const data = ticketData[id];
        if (!data) return null;
        return `CHF ${Number(data.price).toFixed(2)}`;
    };

    const isSoldOut = (id) => {
        const data = ticketData[id];
        return data && data.availability && data.availability.status !== 0;
    };

    const handleLanguageChange = (lang) => {
        navigate(`/${lang}/program`);
    };

    const buildTicketsToAdd = () => {
        if (!formSettings) return [];

        const tickets = [];

        const addTicket = (ticketTypeId, extraOptions = {}) => {
            tickets.push({
                ticketTypeId: parseInt(ticketTypeId),
                quantity: 1,
                salutation: buyerInfo.salutation,
                firstname: buyerInfo.firstName,
                lastname: buyerInfo.name,
                company: buyerInfo.company,
                email: buyerInfo.email,
                phone: buyerInfo.telephone,
                function: buyerInfo.function,
                street: buyerInfo.street + " " + buyerInfo.streetNumber,
                city: buyerInfo.city,
                postCode: buyerInfo.postCode,
                countryId: buyerInfo.countryId || 176,
                country: buyerInfo.countryName || "",
                ...extraOptions,
            });
        };

        // Samstag "Kafi Gipfeli und DV" base — always included, carries all selections as options
        addTicket(formSettings.kafiGipfeliTicketTypeId, {
            option1: buyerInfo.anreise || "",
            option2: prePackage ? getName(parseInt(prePackage)) : "",
            option3: freitagAbend ? getName(formSettings.freitagAbendTicketTypeId) : "",
            option4: nachtwachter ? getName(formSettings.nachtwachterTicketTypeId) : "",
            option5: schiffsausflug ? getName(formSettings.schiffsausflugTicketTypeId) : "",
            option6: nachtessenShow ? getName(formSettings.nachtessenShowTicketTypeId) : "",
            option7: farwell ? getName(formSettings.farwellTicketTypeId) : "",
            option8: anschlussPackage ? getName(parseInt(anschlussPackage)) : "",
            option9: verpflegung,
            option10: hasIntoleranzen ? `Intoleranzen: ${intoleranzen}` : "",
        });

        // Freitag
        if (prePackage) addTicket(prePackage);
        if (freitagAbend) addTicket(formSettings.freitagAbendTicketTypeId);
        if (nachtwachter) addTicket(formSettings.nachtwachterTicketTypeId);

        // Samstag optional
        if (schiffsausflug) addTicket(formSettings.schiffsausflugTicketTypeId);
        if (nachtessenShow) addTicket(formSettings.nachtessenShowTicketTypeId);

        // Sonntag
        if (farwell) addTicket(formSettings.farwellTicketTypeId);
        if (anschlussPackage) addTicket(anschlussPackage);

        // Railway ticket per participant
        if (buyerInfo.isRailway) addTicket(formSettings.railWayTicketTypeId);

        return tickets;
    };

    const ensureValidOrder = async () => {
        let currentOrderId = sessionStorage.getItem("orderId");
        try {
            //check if order still exists
            await axios.get(`${baseUrl}/ShopBasket/Order/${currentOrderId}`);
        } catch {
            //if order no longer exists start new order
            const res = await axios.post(`${baseUrl}/ShopBasket/Order`, {
                affiliateShopId: null,
                currency: "CHF",
                tenantId: 1,
                pointOfSaleId: formSettings.posId,
                abbreviation: "",
                paymentType: 1,
                deliveryType: 1,
            });
            currentOrderId = res.data.id;
            sessionStorage.setItem("orderId", currentOrderId);
        }
        return currentOrderId;
    };

    const saveCurrentTicketsToPending = () => {
        const ticketsToAdd = buildTicketsToAdd();
        const pending = JSON.parse(sessionStorage.getItem("pendingTickets") || "[]");
        pending[companionCount] = ticketsToAdd;
        sessionStorage.setItem("pendingTickets", JSON.stringify(pending));
        return ticketsToAdd;
    };

    const handleAddCompanion = () => {
        if (hasIntoleranzen && !intoleranzen.trim()) {
            setErrors(resources?.FillMandatoryFields);
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }

        saveCurrentTicketsToPending();
        const nextCompanionNumber = companionCount + 1;
        sessionStorage.setItem("companionCount", String(nextCompanionNumber));
        sessionStorage.removeItem("buyerInfo");
        navigate(`/${language}/participant/companion/${nextCompanionNumber}`);
    };

    const handleSubmit = async () => {
        if (isSubmitting) return;

        if (hasIntoleranzen && !intoleranzen.trim()) {
            setErrors(resources?.FillMandatoryFields);
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }

        setIsSubmitting(true);

        try {
            await ensureValidOrder();
            saveCurrentTicketsToPending();
            navigate(`/${language}/accommodation`);
        } catch (error) {
            console.error("Error navigating to accommodation:", error);
            setErrors(resources?.ErrorOccurred);
            setIsSubmitting(false);
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    if (!buyerInfo) return null;

    const buyerName = `${buyerInfo.firstName || ""} ${buyerInfo.name || ""}`.trim();

    const renderPrice = (id) => {
        const price = getPrice(id);
        return price ? <span className="program-price">{price}</span> : null;
    };

    return (
        <div>
            <Header resources={resources} title={resources?.ProgramTitle} language={language} onLanguageChange={handleLanguageChange}
                containerClass="page-container-smaller" />
            <>
                <div className="container page-container">
                    <div className="mt-4 page-container-smaller">

                        {errors !== "" && (
                            <div className="alert alert-danger" role="alert">
                                {errors}
                            </div>
                        )}

                        {buyerName && (
                            <p className="mb-4"><strong>{buyerName}</strong>: {resources?.ProgramParticipationLabel}</p>
                        )}

                        <div className="d-flex justify-content-end mb-2">
                            <span className="small fw-bold">{resources?.ProgramPricePerPerson}</span>
                        </div>

                        {/* ── Freitag, 1. Oktober 2027 ── */}
                        <h4 className="program-day-title">{resources?.ProgramFridayDate}</h4>
                        <hr />

                        {/* Pre-Package (radio) */}
                        <div className="program-section mb-3">
                            <div className="form-check mb-2">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="prePackageOpen"
                                    checked={prePackageOpen}
                                    onChange={(e) => {
                                        setPrePackageOpen(e.target.checked);
                                        if (!e.target.checked) setPrePackage("");
                                    }}
                                />
                                <label className="form-check-label ms-2" htmlFor="prePackageOpen">
                                    <strong>{resources?.ProgramPrePackageTitle}</strong>
                                </label>
                            </div>
                            {prePackageOpen && <div className="ms-4">
                                <div className="form-check d-flex justify-content-between align-items-center mb-2">
                                    <div>
                                        <input
                                            type="radio"
                                            className="form-check-input"
                                            name="prePackage"
                                            id="prePackageRheinfall"
                                            value={formSettings?.prePackageRheinfallTicketTypeId}
                                            checked={prePackage === String(formSettings?.prePackageRheinfallTicketTypeId)}
                                            onChange={(e) => setPrePackage(e.target.value)}
                                            disabled={isSoldOut(formSettings?.prePackageRheinfallTicketTypeId)}
                                        />
                                        <label className="form-check-label ms-2" htmlFor="prePackageRheinfall">
                                            {getName(formSettings?.prePackageRheinfallTicketTypeId)}
                                            {isSoldOut(formSettings?.prePackageRheinfallTicketTypeId) && <span className="text-danger ms-2">({resources?.SoldOut})</span>}
                                        </label>
                                    </div>
                                    {renderPrice(formSettings?.prePackageRheinfallTicketTypeId)}
                                </div>
                                <div className="form-check d-flex justify-content-between align-items-center mb-2">
                                    <div>
                                        <input
                                            type="radio"
                                            className="form-check-input"
                                            name="prePackage"
                                            id="prePackageWeinprobe"
                                            value={formSettings?.prePackageWeinprobeTicketTypeId}
                                            checked={prePackage === String(formSettings?.prePackageWeinprobeTicketTypeId)}
                                            onChange={(e) => setPrePackage(e.target.value)}
                                            disabled={isSoldOut(formSettings?.prePackageWeinprobeTicketTypeId)}
                                        />
                                        <label className="form-check-label ms-2" htmlFor="prePackageWeinprobe">
                                            {getName(formSettings?.prePackageWeinprobeTicketTypeId)}
                                            {isSoldOut(formSettings?.prePackageWeinprobeTicketTypeId) && <span className="text-danger ms-2">({resources?.SoldOut})</span>}
                                        </label>
                                    </div>
                                    {renderPrice(formSettings?.prePackageWeinprobeTicketTypeId)}
                                </div>
                                <div className="form-check d-flex justify-content-between align-items-center mb-2">
                                    <div>
                                        <input
                                            type="radio"
                                            className="form-check-input"
                                            name="prePackage"
                                            id="prePackageFuehrung"
                                            value={formSettings?.prePackageFuehrungSteinTicketTypeId}
                                            checked={prePackage === String(formSettings?.prePackageFuehrungSteinTicketTypeId)}
                                            onChange={(e) => setPrePackage(e.target.value)}
                                            disabled={isSoldOut(formSettings?.prePackageFuehrungSteinTicketTypeId)}
                                        />
                                        <label className="form-check-label ms-2" htmlFor="prePackageFuehrung">
                                            {getName(formSettings?.prePackageFuehrungSteinTicketTypeId)}
                                            {isSoldOut(formSettings?.prePackageFuehrungSteinTicketTypeId) && <span className="text-danger ms-2">({resources?.SoldOut})</span>}
                                        </label>
                                    </div>
                                    {renderPrice(formSettings?.prePackageFuehrungSteinTicketTypeId)}
                                </div>
                            </div>}
                        </div>

                        {/* Freitag-Abend */}
                        <div className="program-section mb-3 d-flex justify-content-between align-items-center">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="freitagAbend"
                                    checked={freitagAbend}
                                    onChange={(e) => setFreitagAbend(e.target.checked)}
                                    disabled={isSoldOut(formSettings?.freitagAbendTicketTypeId)}
                                />
                                <label className="form-check-label ms-2" htmlFor="freitagAbend">
                                    {getName(formSettings?.freitagAbendTicketTypeId)}
                                    {isSoldOut(formSettings?.freitagAbendTicketTypeId) && <span className="text-danger ms-2">({resources?.SoldOut})</span>}
                                </label>
                            </div>
                            {renderPrice(formSettings?.freitagAbendTicketTypeId)}
                        </div>

                        {/* Nachtwächter */}
                        <div className="program-section mb-3 d-flex justify-content-between align-items-center">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="nachtwachter"
                                    checked={nachtwachter}
                                    onChange={(e) => setNachtwachter(e.target.checked)}
                                    disabled={isSoldOut(formSettings?.nachtwachterTicketTypeId)}
                                />
                                <label className="form-check-label ms-2" htmlFor="nachtwachter">
                                    {getName(formSettings?.nachtwachterTicketTypeId)}
                                    {isSoldOut(formSettings?.nachtwachterTicketTypeId) && <span className="text-danger ms-2">({resources?.SoldOut})</span>}
                                </label>
                            </div>
                            {renderPrice(formSettings?.nachtwachterTicketTypeId)}
                        </div>

                        {/* ── Samstag, 2. Oktober 2027 ── */}
                        <h4 className="program-day-title mt-4">{resources?.ProgramSaturdayDate}</h4>
                        <hr />

                        {/* Kafi Gipfeli — base, always included */}
                        <div className="program-section mb-3">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={true}
                                    disabled={true}
                                    readOnly
                                />
                                <div className="form-check-label ms-2" style={{ opacity: 1 }}>
                                    <strong>{getName(formSettings?.kafiGipfeliTicketTypeId)}</strong>
                                    {getDescription(formSettings?.kafiGipfeliTicketTypeId) && (
                                        <div className="d-flex justify-content-between mt-1">
                                            <span className="small">↳ {getDescription(formSettings?.kafiGipfeliTicketTypeId)}</span>
                                            {renderPrice(formSettings?.kafiGipfeliTicketTypeId)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Schiffsausflug */}
                        <div className="program-section mb-3 d-flex justify-content-between align-items-center">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="schiffsausflug"
                                    checked={schiffsausflug}
                                    onChange={(e) => setSchiffsausflug(e.target.checked)}
                                    disabled={isSoldOut(formSettings?.schiffsausflugTicketTypeId)}
                                />
                                <label className="form-check-label ms-2" htmlFor="schiffsausflug">
                                    {getName(formSettings?.schiffsausflugTicketTypeId)}
                                    {isSoldOut(formSettings?.schiffsausflugTicketTypeId) && <span className="text-danger ms-2">({resources?.SoldOut})</span>}
                                </label>
                            </div>
                            {renderPrice(formSettings?.schiffsausflugTicketTypeId)}
                        </div>

                        {/* Nachtessen und Show */}
                        <div className="program-section mb-3 d-flex justify-content-between align-items-center">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="nachtessenShow"
                                    checked={nachtessenShow}
                                    onChange={(e) => setNachtessenShow(e.target.checked)}
                                    disabled={isSoldOut(formSettings?.nachtessenShowTicketTypeId)}
                                />
                                <label className="form-check-label ms-2" htmlFor="nachtessenShow">
                                    {getName(formSettings?.nachtessenShowTicketTypeId)}
                                    {isSoldOut(formSettings?.nachtessenShowTicketTypeId) && <span className="text-danger ms-2">({resources?.SoldOut})</span>}
                                </label>
                            </div>
                            {renderPrice(formSettings?.nachtessenShowTicketTypeId)}
                        </div>

                        {/* ── Sonntag, 3. Oktober 2027 ── */}
                        <h4 className="program-day-title mt-4">{resources?.ProgramSundayDate}</h4>
                        <hr />

                        {/* FarWell */}
                        <div className="program-section mb-3 d-flex justify-content-between align-items-center">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="farwell"
                                    checked={farwell}
                                    onChange={(e) => setFarwell(e.target.checked)}
                                    disabled={isSoldOut(formSettings?.farwellTicketTypeId)}
                                />
                                <label className="form-check-label ms-2" htmlFor="farwell">
                                    {getName(formSettings?.farwellTicketTypeId)}
                                    {isSoldOut(formSettings?.farwellTicketTypeId) && <span className="text-danger ms-2">({resources?.SoldOut})</span>}
                                </label>
                            </div>
                            {renderPrice(formSettings?.farwellTicketTypeId)}
                        </div>

                        {/* Anschluss-Package (radio) */}
                        <div className="program-section mb-4">
                            <div className="form-check mb-2">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="anschlussPackageOpen"
                                    checked={anschlussPackageOpen}
                                    onChange={(e) => {
                                        setAnschlussPackageOpen(e.target.checked);
                                        if (!e.target.checked) setAnschlussPackage("");
                                    }}
                                />
                                <label className="form-check-label ms-2" htmlFor="anschlussPackageOpen">
                                    <strong>{resources?.ProgramAnschlussPackageTitle}</strong>
                                </label>
                            </div>
                            {anschlussPackageOpen && <div className="ms-4">
                                {[
                                    { id: formSettings?.anschlussRheinfallTicketTypeId, htmlFor: "anschlussRheinfall" },
                                    { id: formSettings?.anschlussWeinprobeTicketTypeId, htmlFor: "anschlussWeinprobe" },
                                    { id: formSettings?.anschluss360TicketTypeId, htmlFor: "anschluss360" },
                                    { id: formSettings?.anschlussStadtfuehrungTicketTypeId, htmlFor: "anschlussStadtfuehrung" },
                                    { id: formSettings?.anschlussFuehrungSteinTicketTypeId, htmlFor: "anschlussFuehrungStein" },
                                ].map(({ id, htmlFor }) => (
                                    <div key={htmlFor} className="form-check d-flex justify-content-between align-items-center mb-2">
                                        <div>
                                            <input
                                                type="radio"
                                                className="form-check-input"
                                                name="anschlussPackage"
                                                id={htmlFor}
                                                value={id}
                                                checked={anschlussPackage === String(id)}
                                                onChange={(e) => setAnschlussPackage(e.target.value)}
                                                disabled={isSoldOut(id)}
                                            />
                                            <label className="form-check-label ms-2" htmlFor={htmlFor}>
                                                {getName(id)}
                                                {isSoldOut(id) && <span className="text-danger ms-2">({resources?.SoldOut})</span>}
                                            </label>
                                        </div>
                                        {renderPrice(id)}
                                    </div>
                                ))}
                            </div>}
                        </div>

                        {/* Verpflegung */}
                        <h4 className="program-day-title mt-4">{resources?.ProgramVerpflegungTitle} <span className="text-danger">*</span></h4>
                        <hr />

                        <div className="form-check mb-2">
                            <input type="radio" className="form-check-input" name="verpflegung" id="verpflegungAlles" value="Alles" checked={verpflegung === "Alles"} onChange={(e) => setVerpflegung(e.target.value)} />
                            <label className="form-check-label ms-2" htmlFor="verpflegungAlles">{resources?.ProgramVerpflegungAlles}</label>
                        </div>
                        <div className="form-check mb-2">
                            <input type="radio" className="form-check-input" name="verpflegung" id="verpflegungVegetarisch" value="Vegetarisch" checked={verpflegung === "Vegetarisch"} onChange={(e) => setVerpflegung(e.target.value)} />
                            <label className="form-check-label ms-2" htmlFor="verpflegungVegetarisch">{resources?.ProgramVerpflegungVegetarisch}</label>
                        </div>
                        <div className="form-check mb-2">
                            <input type="radio" className="form-check-input" name="verpflegung" id="verpflegungVegan" value="Vegan" checked={verpflegung === "Vegan"} onChange={(e) => setVerpflegung(e.target.value)} />
                            <label className="form-check-label ms-2" htmlFor="verpflegungVegan">{resources?.ProgramVerpflegungVegan}</label>
                        </div>
                        <div className="form-check mb-2">
                            <input type="checkbox" className="form-check-input" id="verpflegungIntoleranzen" checked={hasIntoleranzen} onChange={(e) => setHasIntoleranzen(e.target.checked)} />
                            <label className="form-check-label ms-2" htmlFor="verpflegungIntoleranzen">{resources?.ProgramIntoleranzenLabel}</label>
                        </div>
                        {hasIntoleranzen && (
                            <input
                                type="text"
                                className="form-control mb-2"
                                value={intoleranzen}
                                onChange={(e) => setIntoleranzen(e.target.value)}
                            />
                        )}

                        {companionCount < formSettings?.maxCompanions && (
                            <div className="row mt-4">
                                <div className="col-12 text-center">
                                    <button
                                        className="btn btn-custom-transparent"
                                        onClick={handleAddCompanion}
                                        disabled={isSubmitting}
                                    >
                                        {resources?.ProgramAddCompanionButton}
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="row mt-5">
                            <div className="col-12 d-flex justify-content-between">
                                {companionCount === 0 && (
                                    <button
                                        className="btn btn-custom"
                                        onClick={() => navigate(`/${language}/participant`)}
                                        disabled={isSubmitting}
                                    >
                                        {resources?._Back}
                                    </button>
                                )}
                                <button
                                    className={`btn btn-custom ${companionCount === 0 ? "" : "ms-auto"}`}
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                >
                                    {resources?._Next}
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
                <Footer language={language} />
            </>
        </div>
    );
};

export default Program;
